/**
 * @module boardUtils
 * @description Board bounding-box detection, tile counting, tile extraction,
 * board-click handling, and board-clear operations for the Game component.
 */

/**
 * Calculates the adjusted bounding box of the board element, accounting for
 * tile-centering offsets.
 * @param {number} boardScale - The current pixel size of one board cell.
 * @returns {{ left: number, right: number, top: number, bottom: number } | null}
 */
export function getBoardBoundingBox(boardScale) {
    const currentBoardBounding = document
        .querySelector(".board")
        ?.getBoundingClientRect();

    if (!currentBoardBounding) return null;

    const boardBounding = {
        left: currentBoardBounding.left,
        right: currentBoardBounding.right,
        top: currentBoardBounding.top,
        bottom: currentBoardBounding.bottom,
    };

    boardBounding.left = boardBounding.left + (boardScale / 2) - (3 * boardScale / 100);
    boardBounding.right = boardBounding.right - (boardScale / 2) - (3 * boardScale / 100);
    boardBounding.top = boardBounding.top + (boardScale / 2) - (3 * boardScale / 100);
    boardBounding.bottom = boardBounding.bottom - (boardScale / 2) - (3 * boardScale / 100);

    return boardBounding;
}

/**
 * Counts the number of tiles marked as newly placed on the board.
 * @param {object} board - The game board object.
 * @returns {number}
 */
export function countPlayedTiles(board) {
    return board?.rows?.reduce((count, row) => {
        return count + row.spaces.reduce((subCount, space) => {
            return subCount + (space.isNew ? 1 : 0);
        }, 0);
    }, 0);
}

/**
 * Extracts an array of newly placed tile coordinates and values from the board.
 * Used by getScore and playWord to build the payload for SignalR calls.
 * @param {object} board - The game board object.
 * @returns {Array<{ x: number, y: number, tile: object }>}
 */
export function extractBoardTiles(board) {
    const boardTiles = [];
    for (let r = 0; r < board.rows.length; r++) {
        for (let s = 0; s < board.rows.length; s++) {
            if (board.rows[r].spaces[s].isNew) {
                boardTiles.push({ x: r, y: s, tile: board.rows[r].spaces[s].tile.tile });
            }
        }
    }
    return boardTiles;
}

/**
 * @typedef {object} ClickBoardTileDeps
 * @property {Array} rackTiles - Current rack tiles.
 * @property {object} game - Current game state.
 * @property {function(Array): void} setRackTiles - Rack tiles setter.
 * @property {function(object): void} setGame - Game state setter.
 * @property {function(object): void} setFloatingTile - Floating tile setter.
 * @property {function(): void} getScore - Triggers server score recalculation.
 * @property {function(number): void} setPlayedTileCount - Played tile count setter.
 * @property {boolean} isMyTurn - If false, no board mutation occurs.
 */

/**
 * Handles clicking a board space — picks up an existing isNew tile as a floating
 * tile, or places the first rack tile onto an empty space.
 * @param {object} space - The board space that was clicked.
 * @param {ClickBoardTileDeps} deps
 * @returns {void}
 */
export function clickBoardTile(space, deps) {
    const { rackTiles, game, setRackTiles, setGame, setFloatingTile, getScore, setPlayedTileCount, isMyTurn } = deps;
    if (!isMyTurn) return;
    if (space.tile && space.isNew) {
        // pick up tile — only for newly placed tiles this turn
        console.log("SpaceTile", space.tile);
        if (space.tile.tile.value === 0) {
            space.tile.tile.letter = "";
        }
        setFloatingTile(space.tile);
        game.board.rows[space.x].spaces[space.y].tile = null;
        game.board.rows[space.x].spaces[space.y].isNew = false;
        game.board.rows[space.x].spaces[space.y].round = null;
        game.board.rows[space.x].spaces[space.y].playerIndex = null;
        setGame({ ...game });
        getScore();
    } else {
        // place first tile from rack
        if (rackTiles.length) {
            console.log("Current Rack", rackTiles);
            let tileToPlay = rackTiles.shift();
            console.log("TILE TO PLAY", tileToPlay, rackTiles);
            setRackTiles([...rackTiles]);
            space.tile = tileToPlay;
            space.isNew = true;
            space.round = game.currentRound;
            space.playerIndex = game.currentPlayer;
            setGame({ ...game, rack: rackTiles });
            getScore();
        }
    }
    setPlayedTileCount(countPlayedTiles(game.board));
}

/**
 * @typedef {object} ClearTilesFromBoardDeps
 * @property {object} game - Current game state.
 * @property {Array} rackTiles - Current rack tiles.
 * @property {function(Array): void} setRackTiles - Rack tiles setter.
 * @property {function(object): void} setGame - Game state setter.
 * @property {function(number): void} setPlayedTileCount - Played tile count setter.
 */

/**
 * Removes all isNew tiles from the board and returns them to the front of the rack,
 * adjusting positions to prevent overlap.
 * @param {ClearTilesFromBoardDeps} deps
 * @returns {void}
 */
export function clearTilesFromBoard(deps) {
    const { game, rackTiles, setRackTiles, setGame, setPlayedTileCount } = deps;
    console.log("CLEAR TILES FROM BOARD");
    const tilesFromBoard = [];
    for (let y = game.board.rows.length - 1; y >= 0; y--) {
        for (let x = game.board.rows.length - 1; x >= 0; x--) {
            if (game.board.rows[y].spaces[x].isNew) {
                var tile = game.board.rows[y].spaces[x].tile;
                tilesFromBoard.push(tile);
                game.board.rows[y].spaces[x].tile = null;
                game.board.rows[y].spaces[x].isNew = false;
                game.board.rows[y].spaces[x].round = 0;
                game.board.rows[y].spaces[x].playerIndex = 0;
            }
        }
    }
    //TODO: Change this so that the tiles are added to the end of the rack
    const newRackTiles = [...rackTiles];
    tilesFromBoard.forEach(tile => {
        tile.position = 0;
        newRackTiles.unshift(tile);
    });
    for (let i = 0; i < newRackTiles.length; i++) {
        if (newRackTiles[i].position < i * 75) {
            newRackTiles[i].position = i * 75;
        }
    }
    setRackTiles(newRackTiles);
    setGame({ ...game, rack: newRackTiles });
    setPlayedTileCount(countPlayedTiles(game.board));
}
